import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Expander, ExpanderTitleRendererParams } from '@amzn/stencil-react-components/expander'
import { Col, Hr, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { WithModal } from '@amzn/stencil-react-components/modal'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { H4, Text } from '@amzn/stencil-react-components/text'

import { BetaChip } from 'src/components/BetaChip'
import { ClickableH4 } from 'src/components/ExpanderHeadings'
import { ModalContent } from 'src/components/ModalContent'
import {
    ModuleAIReviewContext,
    ModuleAIReviewContextProps,
} from 'src/contexts/ModuleAIReviewContext'
import { useAsyncWithReload } from 'src/pages/module-review/index'
import { buildAIReviewCsv } from 'src/pages/module-review/moduleAIReviewCsv'
import {
    ResolvedAIReviewFinding,
    ResolvedAIReviewSection,
    resolveSectionPages,
    sectionsForPage,
} from 'src/pages/module-review/moduleAIReviewPages'
import {
    ModuleAIReview,
    ModuleAIReviewService,
    ModuleAIReviewSeverity,
    ModuleAIReviewStatus,
} from 'src/services/backend/ModuleAIReviewService'
import { downloadAsFile } from 'src/services/downloadAsFile'

export const AI_REVIEW_POLL_INTERVAL_MS = 5000
/** How long to wait for a review to start after the page loads, e.g. right after a draft save. */
// eslint-disable-next-line no-magic-numbers
export const AI_REVIEW_START_WAIT_MS = 30 * 1000
/** A review still running this long after it started is treated as stuck. */
// eslint-disable-next-line no-magic-numbers
export const AI_REVIEW_STUCK_AFTER_MS = 4 * 60 * 1000

const RUNNING_STATUSES: ModuleAIReviewStatus[] = ['IN_PROGRESS']

const SEVERITY_LABEL: Record<ResolvedAIReviewFinding['severity'], string> = {
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
}

/**
 * True when a review has been running for longer than it can take. Counts from `loadedAt` when the
 * review has no `startedAt`, so polling still stops.
 */
export const isReviewStuck = (
    aiReview: ModuleAIReview | undefined,
    now: number,
    loadedAt: number
) =>
    aiReview?.status === 'IN_PROGRESS' &&
    now - (aiReview.startedAt ?? loadedAt) > AI_REVIEW_STUCK_AFTER_MS

const countFindings = (
    sections: readonly ResolvedAIReviewSection[],
    severity?: ModuleAIReviewSeverity
) =>
    sections.reduce(
        (total, s) => total + s.findings.filter((f) => !severity || f.severity === severity).length,
        0
    )

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

/**
 * Loads the advisory AI review of a module version's latest saved draft for
 * {@link ModuleAIReviewContext}. The request is keyed on the route param so it runs in parallel with
 * the module load, and it re-polls while the review is running. Page numbers are resolved against
 * the module once both have loaded.
 */
export function useModuleAIReview({
    isEnabled,
    moduleVersionId,
    workflow,
}: {
    isEnabled: boolean
    moduleVersionId: string
    workflow: readonly unknown[]
}): ModuleAIReviewContextProps {
    const {
        data: aiReview,
        isLoading,
        error,
        reload,
    } = useAsyncWithReload<ModuleAIReview | undefined>(
        async () =>
            isEnabled ? await ModuleAIReviewService.getModuleAIReview(moduleVersionId) : undefined,
        [isEnabled, moduleVersionId]
    )

    const loadedAt = useRef(Date.now())
    useEffect(() => {
        loadedAt.current = Date.now()
    }, [moduleVersionId])

    const now = Date.now()
    const isStuck = isReviewStuck(aiReview, now, loadedAt.current)
    const isRunning = !!aiReview && RUNNING_STATUSES.includes(aiReview.status) && !isStuck
    const isWaitingToStart =
        aiReview?.status === 'NOT_REQUESTED' && now - loadedAt.current < AI_REVIEW_START_WAIT_MS
    const shouldPoll = isRunning || isWaitingToStart
    useEffect(() => {
        if (!shouldPoll) {
            return
        }
        const timer = setTimeout(reload, AI_REVIEW_POLL_INTERVAL_MS)
        return () => clearTimeout(timer)
    }, [shouldPoll, aiReview, reload])

    const sections = useMemo(
        () => resolveSectionPages(aiReview?.sections ?? [], workflow),
        [aiReview, workflow]
    )

    return useMemo(
        () => ({ isEnabled, aiReview, sections, isLoading, error, isStuck }),
        [isEnabled, aiReview, sections, isLoading, error, isStuck]
    )
}

function AIReviewFindingRow({ finding }: { finding: ResolvedAIReviewFinding }) {
    return (
        <Row gridGap='S200' dataTestId='ai-review-finding'>
            <Text fontWeight='bold'>{SEVERITY_LABEL[finding.severity]}</Text>
            <Col gridGap='S100'>
                <Text>{finding.message}</Text>
                <Text fontSize='T100' color='neutral70' dataTestId='ai-review-finding-meta'>
                    {[
                        finding.pageNumber ? `Page ${finding.pageNumber}` : 'Module-wide',
                        finding.ruleId,
                        finding.itemLabel ?? finding.entityId,
                        finding.locales.join(', '),
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </Text>
            </Col>
        </Row>
    )
}

function AIReviewSectionExpander({
    section,
    shouldExpandOnMount,
}: {
    section: ResolvedAIReviewSection
    shouldExpandOnMount: boolean
}) {
    const renderTitle = useCallback(
        ({ titleText, toggle }: ExpanderTitleRendererParams) => (
            <ClickableH4 onClick={toggle}>{titleText}</ClickableH4>
        ),
        []
    )
    return (
        <View dataTestId={`ai-review-section-${section.sectionId}`}>
            <Expander
                titleText={`${section.title ?? section.sectionId} (${section.findings.length})`}
                renderTitle={renderTitle}
                shouldExpandOnMount={shouldExpandOnMount}
            >
                <Col gridGap='S300' padding={{ left: 'S300', top: 'S200', bottom: 'S200' }}>
                    {section.findings.map((finding) => (
                        <AIReviewFindingRow key={finding.findingId} finding={finding} />
                    ))}
                </Col>
            </Expander>
        </View>
    )
}

/**
 * The AI review comments, one collapsible section per rubric area. Narrowed to one page when
 * `pageNumber` is set, in which case the sections start expanded since there are few of them.
 */
export function ModuleAIReviewModalContent({
    close,
    pageNumber,
}: {
    close: () => void
    pageNumber?: number
}) {
    const { sections } = useContext(ModuleAIReviewContext)
    const visibleSections = useMemo(
        () =>
            pageNumber === undefined
                ? sections.filter((s) => s.findings.length > 0)
                : sectionsForPage(sections, pageNumber),
        [sections, pageNumber]
    )

    return (
        <ModalContent
            titleText={
                pageNumber === undefined
                    ? 'AI review comments'
                    : `AI review comments - Page ${pageNumber}`
            }
            titleAdornment={<BetaChip dataTestId='ai-review-beta-chip' />}
            maxWidth='800px'
            buttons={[
                <Button key='close-ai-review' dataTestId='close-ai-review-modal' onClick={close}>
                    Close
                </Button>,
            ]}
        >
            <Col gridGap='S300' dataTestId='ai-review-modal'>
                <MessageBanner type={MessageBannerType.Informational}>
                    These comments are advisory and do not block review or approval.
                </MessageBanner>
                {visibleSections.length === 0 ? (
                    <Text dataTestId='ai-review-modal-empty'>
                        {pageNumber === undefined
                            ? 'No issues found.'
                            : 'No AI review comments for this page.'}
                    </Text>
                ) : (
                    visibleSections.map((section) => (
                        <AIReviewSectionExpander
                            key={section.sectionId}
                            section={section}
                            shouldExpandOnMount={pageNumber !== undefined}
                        />
                    ))
                )}
            </Col>
        </ModalContent>
    )
}

function ModuleAIReviewReportSummary({
    aiReview,
    sections,
    moduleName,
}: {
    aiReview: ModuleAIReview
    sections: readonly ResolvedAIReviewSection[]
    moduleName: string
}) {
    const { summary } = aiReview
    const renderModal = useCallback(
        ({ close }: { close: () => void }) => <ModuleAIReviewModalContent close={close} />,
        []
    )
    const exportCsv = useCallback(
        () =>
            downloadAsFile(
                new Blob([buildAIReviewCsv(sections)], { type: 'text/csv' }),
                `${moduleName} - AI review.csv`
            ),
        [sections, moduleName]
    )
    // Counted from the collapsed sections, so a problem found in several locales counts once.
    const findingCount = countFindings(sections)
    const [high, medium, low] = (['HIGH', 'MEDIUM', 'LOW'] as const).map((severity) =>
        countFindings(sections, severity)
    )
    return (
        <Col gridGap='S300' dataTestId='ai-review-report'>
            {summary?.model?.status === 'SKIPPED_TOO_LARGE' && (
                <MessageBanner
                    type={MessageBannerType.Informational}
                    dataTestId='ai-review-model-skipped'
                >
                    This module is too large for the AI model, so only the automated checks ran.
                </MessageBanner>
            )}
            <Text dataTestId='ai-review-summary'>
                {`${pluralize(
                    findingCount,
                    'comment'
                )}: ${high} high, ${medium} medium, ${low} low.`}
            </Text>
            <Row gridGap='S200'>
                <WithModal renderModal={renderModal}>
                    {({ open }) => (
                        <Button
                            onClick={open}
                            variant={ButtonVariant.Secondary}
                            size={ButtonSize.Small}
                            dataTestId='open-ai-review-modal'
                        >
                            {`View AI review comments (${findingCount})`}
                        </Button>
                    )}
                </WithModal>
                {findingCount > 0 && (
                    <Button
                        onClick={exportCsv}
                        variant={ButtonVariant.Secondary}
                        size={ButtonSize.Small}
                        dataTestId='export-ai-review-csv'
                    >
                        Export CSV
                    </Button>
                )}
            </Row>
        </Col>
    )
}

function ModuleAIReviewStatusContent({ moduleName }: { moduleName: string }) {
    const { aiReview, sections, error, isStuck } = useContext(ModuleAIReviewContext)

    if (error) {
        return (
            <MessageBanner type={MessageBannerType.Error} dataTestId='ai-review-error'>
                Unable to load the AI review: {error.message}
            </MessageBanner>
        )
    }

    if (!aiReview || aiReview.status === 'NOT_REQUESTED') {
        return (
            <MessageBanner type={MessageBannerType.Informational} dataTestId='ai-review-not-ready'>
                No AI review yet. It runs each time a draft of this module is saved.
            </MessageBanner>
        )
    }

    if (isStuck) {
        return (
            <MessageBanner type={MessageBannerType.Warning} dataTestId='ai-review-stuck'>
                The AI review is taking longer than expected. Save the draft again to restart it.
            </MessageBanner>
        )
    }

    if (RUNNING_STATUSES.includes(aiReview.status)) {
        return (
            <MessageBanner type={MessageBannerType.Informational} dataTestId='ai-review-running'>
                The AI review is running. It usually takes a few minutes, and the results will
                appear here automatically.
            </MessageBanner>
        )
    }

    if (aiReview.status === 'FAILED') {
        return (
            <MessageBanner type={MessageBannerType.Warning} dataTestId='ai-review-failed'>
                The AI review could not be completed
                {aiReview.failureReason ? `: ${aiReview.failureReason}` : '.'}
            </MessageBanner>
        )
    }

    return (
        <ModuleAIReviewReportSummary
            aiReview={aiReview}
            sections={sections}
            moduleName={moduleName}
        />
    )
}

/**
 * The AI review box of the module viewer: its status, or a summary with buttons that open the
 * comments and export them as CSV. Advisory only.
 */
export function ModuleAIReviewPanel({ moduleName }: { moduleName: string }) {
    const { isEnabled, aiReview, isLoading } = useContext(ModuleAIReviewContext)
    if (!isEnabled) {
        return null
    }
    return (
        <View dataTestId='module-ai-review-panel'>
            <Row alignItems='center' gridGap='S200'>
                <H4 fontSize='T400'>AI review</H4>
                <BetaChip dataTestId='ai-review-beta-chip' />
            </Row>
            <Hr />
            <Spacer height='S300' />
            {isLoading && !aiReview ? (
                <Spinner dataTestId='ai-review-loading-spinner' />
            ) : (
                <ModuleAIReviewStatusContent moduleName={moduleName} />
            )}
        </View>
    )
}

/**
 * Sits beside a page card's "(x comments)" button and opens only that page's AI review comments.
 * Renders nothing until a completed AI review is available.
 */
function AIReviewCommentsButtonInner({ pageNumber }: { pageNumber: number }) {
    const { isEnabled, aiReview, sections } = useContext(ModuleAIReviewContext)
    const count = useMemo(
        () => countFindings(sectionsForPage(sections, pageNumber)),
        [sections, pageNumber]
    )
    const renderModal = useCallback(
        ({ close }: { close: () => void }) => (
            <ModuleAIReviewModalContent close={close} pageNumber={pageNumber} />
        ),
        [pageNumber]
    )

    if (!isEnabled || aiReview?.status !== 'COMPLETED') {
        return null
    }

    return (
        <WithModal renderModal={renderModal}>
            {({ open }) => (
                <span style={{ alignSelf: 'center' }}>
                    <Button
                        onClick={open}
                        variant={ButtonVariant.Tertiary}
                        size={ButtonSize.Small}
                        dataTestId={`open-ai-review-page-${pageNumber}`}
                        title='View AI review comments for this page'
                    >
                        {`(${pluralize(count, 'AI comment')})`}
                    </Button>
                </span>
            )}
        </WithModal>
    )
}

export const AIReviewCommentsButton = React.memo(AIReviewCommentsButtonInner)
